import { EventEmitter, Input, Output } from '@angular/core';

import { ThfModalAction } from '@totvs/thf-ui';

import { convertToBoolean, isExternalLink, isTypeof } from '../../utils/util';

import { ThfPageChangePasswordRecovery } from './interfaces/thf-page-change-password-recovery.interface';
import { ThfPageChangePasswordRequirement } from './interfaces/thf-page-change-password-requirement.interface';

/**
 * @description
 *
 * O componente `thf-page-change-password` Ã© utilizado como template para tela de cadastro ou alteraÃ§Ã£o de senha.
 *
 * Apresenta dicas e regras para senhas mais seguras e tambÃ©m possibilidade de personalizar o redirecionamento para as telas
 * 'esqueceu a senha', 'voltar' e 'entrar no sistema'. Os textos das telas sÃ£o prÃ©-definidos e imutÃ¡veis.
 *
 * A propriedade `t-url-new-password` automatiza a rotina do template e simplifica o processo de cadastro/alteraÃ§Ã£o de senha, bastando
 * definir uma url para POST das informaÃ§Ãµes digitadas pelo usuÃ¡rio.  A flexibilidade e praticidade podem chegar a um nÃ­vel em que o
 * desenvolvimento da aplicaÃ§Ã£o no *client side* Ã© desprovida de qualquer cÃ³digo-fonte relacionado Ã  rotina de cadastro/alteraÃ§Ã£o de senha.
 * Seu detalhamento para uso pode ser visto logo abaixo em *propriedades*.
 * Caso julgue necessÃ¡rio, pode-se tambÃ©m definir manualmente a rotina do componente.
 *
 * Para que as imagens sejam exibidas corretamente, Ã© necessÃ¡rio incluir o caminho delas ao projeto. Para isso, edite
 * o *assets* no arquivo **angular.json** da aplicaÃ§Ã£o na seguinte ordem:
 * ```
 *   "assets": [
 *     "src/assets",
 *     "src/favicon.ico",
 *     {
 *       "glob": "**\/*",
 *       "input": "node_modules/@totvs/thf-theme/images",
 *       "output": "assets/images"
 *     }
 *   ]
 * ```
 */
export abstract class ThfPageChangePasswordBaseComponent {

  private _hideCurrentPassword: boolean = false;
  private _recovery: string | ThfPageChangePasswordRecovery | Function;
  private _requirements: Array<ThfPageChangePasswordRequirement> = [];
  private _urlHome: string = '/';
  protected validatorChange: any;

  confirmPassword: string;
  currentPassword: string;
  modalAction: ThfModalAction;
  newPassword: string;
  recoveryUrlType: string;
  showRequirements: boolean;

  /**
   * @optional
   *
   * @description
   *
   * Esconde o campo `Senha atual` para que o template seja para criaÃ§Ã£o de senha.
   *
   * @default `false`
   */
  @Input('t-hide-current-password') set hideCurrentPassword(value: boolean) {
    this._hideCurrentPassword = convertToBoolean(value);
  }

  get hideCurrentPassword(): boolean {
    return this._hideCurrentPassword;
  }

  /**
   * @optional
   *
   * @description
   *
   * Caminho para a logomarca localizada na parte superior.
   *
   * > Caso seja indefinida o espaÃ§o se mantÃ©m preservado porÃ©m vazio.
   */
  @Input('t-logo') logo?: string;

  /**
   * @optional
   *
   * @description
   *
   * URL para a aÃ§Ã£o do link `Esqueceu a senha`.
   *
   * A propriedade aceita os seguintes tipos:
   *
   * - **String**: informe uma url externa ou uma rota vÃ¡lida;
   * - **Function**: pode-se customizar a aÃ§Ã£o. Para esta possilidade basta atribuir:
   * ```
   * <thf-page-change-password>
   *      [recovery]="this.myFunc.bind(this)";
   * </thf-page-change-password>
   * ```
   *
   * - **ThfPageChangePasswordRecovery**: cria-se vÃ­nculo automÃ¡tico com o template **thf-modal-password-recovery**.
   *   O objeto deve conter a **url** para requisiÃ§Ã£o dos recursos e pode-se definir o **tipo** de modal para recuperaÃ§Ã£o de senha,
   *   **email** para contato e **mÃ¡scara** do campo de telefone.
   *
   * > Caso nÃ£o tenha valor o link `Esqueceu a senha` desaparece.
   */
  @Input('t-recovery') set recovery(value: string | Function | ThfPageChangePasswordRecovery) {
    this._recovery = value;

    if (isTypeof(value, 'string')) {
      this.recoveryUrlType = isExternalLink(value) ? 'externalLink' : 'internalLink';
    }
  }

  get recovery() {
    return this._recovery;
  }

  /**
   * @optional
   *
   * @description
   *
   * Lista de regras para criaÃ§Ã£o e alteraÃ§Ã£o de senha.
   */
  @Input('t-requirements') set requirements(value: Array<ThfPageChangePasswordRequirement>) {
    this._requirements = value || [];
    this.showRequirements = this._requirements.length > 0;
  }
  get requirements() {
    return this._requirements;
  }

  /**
   * @optional
   *
   * @description
   *
   * Caminho para a logomarca localizada no rodapÃ©.
   */
  @Input('t-secondary-logo') secondaryLogo?: string;

  /**
   * Token para solicitaÃ§Ã£o de troca/recuperaÃ§Ã£o de senha.
   *
   * > Esta propriedade serÃ¡ ignorada caso exista um token como parÃ¢metro na URL inicial do template.
   */
  @Input('t-token') token?: string;

  /**
   * @optional
   *
   * @description
   *
   * URL para a aÃ§Ã£o de retorno da pÃ¡gina.
   *
   * > O botÃ£o `Voltar` aparece apenas para telas de alteraÃ§Ã£o de senha, ou seja, sÃ³ aparece se a propriedade `t-hide-current-password` for
   * falsa.
   *
   * @default `/`
   */
  @Input('t-url-back') urlBack: string = '/';

  /**
   * Endpoint usado pelo template para realizar um POST. Quando preenchido, o mÃ©todo `t-submit` serÃ¡ ignorado e o componente adquirirÃ¡
   * automatizaÃ§Ã£o para o processo de cadastro/troca de senha.
   *
   * ### Processo
   * Ao digitar um valor vÃ¡lido nos campos de senha e pressionar **salvar**,
   * o componente farÃ¡ uma requisiÃ§Ã£o `POST` na url especificada nesta propriedade passando o objeto contendo os valores definidos pelo
   * usuÃ¡rio.
   *
   * ```
   * body {
   *  token?: token,
   *  oldPassword?: oldPassword,
   *  newPassword: newPassword
   * }
   * ```
   *
   * O cÃ³digo de resposta HTTP de status esperado Ã© `204`.
   *
   * Em caso de **sucesso**, serÃ¡ exibida a modal de confirmaÃ§Ã£o de senha alterada.
   *
   * > O token serÃ¡ informado pela propriedade `t-token`do componente ou por um *query parameter* na URL do template.
   *
   * *Processo finalizado.*
   *
   * _______________
   *
   * #### Praticidade
   * As informaÃ§Ãµes do serviÃ§o de autenticaÃ§Ã£o tambÃ©m podem ser transmitidas diretamente pelas configuraÃ§Ã£os de rota e, desta maneira,
   * dispensa-se qualquer menÃ§Ã£o e/ou importaÃ§Ã£o do componente `thf-page-change-password` no restante da aplicaÃ§Ã£o. O exemplo abaixo
   * exemplifica a forma dinÃ¢mica com a qual o template de tela de troca de senha pode ser gerado ao navegar para rota `/change-password`, e
   * tambÃ©m como ele se comunica com o serviÃ§o para efetuaÃ§Ã£o do processo de troca de senha do usuÃ¡rio e solicitaÃ§Ã£o de nova senha.
   * Basta definir nas configuraÃ§Ãµes de rota:
   *
   *
   * ```
   *   import { ThfModalPasswordRecoveryType, ThfPageChangePasswordComponent } from '@totvs/thf-templates';
   *
   *   ...
   *   const routes: Routes = [
   *     {
   *       path: 'change-password', component: ThfPageChangePasswordComponent, data: {
   *         serviceApi: 'https://thf.totvs.com.br/sample/api/new-password',
   *         recovery: {
   *           url: 'https://thf.totvs.com.br/sample/api/users',
   *           type: ThfModalPasswordRecoveryType.All,
   *           contactMail: 'dev.totvs@totvs.com',
   *           phoneMask: '9-999-999-9999'
   *         }
   *       }
   *     }
   *     ...
   *   ];
   *
   *   @NgModule({
   *     imports: [RouterModule.forRoot(routes)],
   *     exports: [RouterModule]
   *   })
   *   export class AppRoutingModule { }
   * ```
   *
   *
   * O metadado `serviceApi` deve ser a **url** para requisiÃ§Ã£o dos recursos de troca de senha. E `recovery` Ã© a interface
   * `ThfPageChangePasswordRecovery` responsÃ¡vel pelas especificaÃ§Ãµes contidas na modal de recuperaÃ§Ã£o de senha.
   *
   * > Ã essencial que siga a nomenclatura dos atributos exemplificados acima para sua efetiva funcionalidade.
   *
   */
  @Input('t-url-new-password') urlNewPassword?: string;

  /**
   * @optional
   *
   * @description
   *
   * URL para a aÃ§Ã£o do botÃ£o `Entrar no sistema` da modal de confirmaÃ§Ã£o que aparece apÃ³s salvar a senha ou se chamada pelo mÃ©todo
   * `openConfirmation`.
   *
   * @default `/`
   */
  @Input('t-url-home') set urlHome(value: string) {
    this._urlHome = value;
    this.modalAction.action = this.navigateTo.bind(this, this.urlHome);
  }
  get urlHome() {
    return this._urlHome;
  }

  /**
   * @optional
   *
   * @description
   *
   * FunÃ§Ã£o executada ao submeter o form pelo botÃ£o salvar.
   *
   * Caso definida essa funÃ§Ã£o, a modal de confirmaÃ§Ã£o nÃ£o aparece, mas pode ser chamada pelo
   * mÃ©todo `openConfirmation`. Exemplo:
   *
   * ```
   * @ViewChild(ThfPageChangePasswordComponent) changePassword: ThfPageChangePasswordComponent;
   *
   * onSubmit() {
   *  this.changePassword.openConfirmation();
   * }
   *
   * ```
   * > Esta propriedade serÃ¡ ignorada se for definido valor para a propriedade `t-url-new-password`.
   */
  @Output('t-submit') submit: EventEmitter<any> = new EventEmitter();

  abstract navigateTo(url: string): void;

}
